import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import RepoCard from './components/RepoCard';
import LoadingSpinner from './components/LoadingSpinner';
import RepoSkeleton from './components/RepoSkeleton';
import ErrorMessage from './components/ErrorMessage';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { fetchGitHubRepos } from './services/githubApi';
import { getCurrentDate, getDateBefore, formatDateForDisplay } from './utils/dateUtils';

const App = () => {
  // State management
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isDark, setIsDark] = useState(false);
  
  // Date selection: default to today, allow user to change current date (max = today)
  const todayRaw = getCurrentDate();
  const [selectedDate, setSelectedDate] = useState(todayRaw);

  // Compute start date as 10 days before the selected date
  const rawStartDate = getDateBefore(selectedDate, 10);

  // Format dates for display
  const formattedCurrentDate = formatDateForDisplay(selectedDate);
  const formattedStartDate = formatDateForDisplay(rawStartDate);

  // Load repositories function
  const loadRepos = useCallback(async (pageNum, startDateParam = rawStartDate) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const data = await fetchGitHubRepos(startDateParam, pageNum);
      
      if (data.items.length === 0) {
        setHasMore(false);
      } else {
        setRepos(prev => pageNum === 1 ? data.items : [...prev, ...data.items]);
        setHasMore(data.items.length === 30);
      }
      setLastUpdated(new Date().toISOString());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, [loading, rawStartDate]);

  // Initial load
  useEffect(() => {
    loadRepos(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user changes the selected date, reload page 1 for the new date range
  const handleDateChange = (newDate) => {
    // clamp to today
    const clamped = newDate > todayRaw ? todayRaw : newDate;
    setSelectedDate(clamped);
    const newStart = getDateBefore(clamped, 10);
    loadRepos(1, newStart);
  };

  // theme effect: toggle dark class on root
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // initialize theme from localStorage or system preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') {
        setIsDark(saved === 'dark');
      } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setIsDark(true);
      }
    } catch (e) {
      // ignore (localStorage not available)
    }
  }, []);

  // persist theme changes
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch (e) {
      // ignore
    }
  }, [isDark]);

  const handleRefresh = () => {
    // Reset selected date to default (today) and reload page 1 for that date range
    const newSelected = todayRaw;
    setSelectedDate(newSelected);
    const newStart = getDateBefore(newSelected, 10);
    loadRepos(1, newStart);
  };

  const handleToggleTheme = () => setIsDark(v => !v);

  const loadMore = useCallback(() => {
    const PER_PAGE = 30;
    const nextPage = Math.floor(repos.length / PER_PAGE) + 1;
    loadRepos(nextPage);
  }, [loadRepos, repos.length]);

  // Infinite scroll ref
  const lastRepoRef = useInfiniteScroll(loading, hasMore, loadMore);

  return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Header 
        startDate={formattedStartDate} 
        currentDate={formattedCurrentDate}
        repoCount={repos.length}
        onRefresh={handleRefresh}
        lastUpdated={lastUpdated}
        onToggleTheme={handleToggleTheme}
        isDark={isDark}
        selectedDate={selectedDate}
        maxDate={todayRaw}
        onDateChange={handleDateChange}
      />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* ARIA live region for loading/error announcements */}
        <div className="sr-only" aria-live="polite">
          {loading ? 'Loading repositories' : error ? `Error: ${error}` : ''}
        </div>
        {/* Error Display */}
        {error && <ErrorMessage message={error} />}

        {/* Repository List */}
        <div className="space-y-4">
          {repos.map((repo, index) => {
            const isLastRepo = repos.length === index + 1;
            return (
              <RepoCard
                key={`${repo.id}-${index}`}
                repo={repo}
                innerRef={isLastRepo ? lastRepoRef : null}
              />
            );
          })}
        </div>

        {/* Loading State */}
        {loading && repos.length === 0 && <LoadingSpinner />}
        {loading && repos.length > 0 && (
          <div className="space-y-4 mt-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <RepoSkeleton key={`skeleton-${i}`} />
            ))}
          </div>
        )}

        {/* Load more fallback button for accessibility and slow networks */}
        {hasMore && !loading && (
          <div className="text-center py-6">
            <button
              type="button"
              onClick={loadMore}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Load more
            </button>
          </div>
        )}

        {/* No More Results */}
        {!hasMore && repos.length > 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 font-medium">No more repositories to load</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && repos.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No repositories found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
